import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
// @mui
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
// auth
import { useAuthContext } from 'src/auth/hooks';
// utils
import axiosInstance, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const ASN_PATTERN = /^\d{9}$/;

// Pick a student by name or ASN and view the app as them (#605). Uses the
// off-campus student search; the server confirms the ASN in PowerSchool.
export default function ViewAsStudentDialog({ open, onClose }) {
  const { startImpersonation } = useAuthContext();
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      setInputValue('');
      setSelected(null);
      setError('');
      setStarting(false);
    }
    return () => clearTimeout(searchTimer.current);
  }, [open]);

  const handleInputChange = useCallback((_, value) => {
    setInputValue(value);
    setError('');
    clearTimeout(searchTimer.current);
    if (value.length < 2) {
      setOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.offCampus.studentSearch(value));
        setOptions(res.data || []);
      } catch (_) {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  }, []);

  // A picked student, or a typed 9-digit ASN
  const asn = selected?.asn || (ASN_PATTERN.test(inputValue.trim()) ? inputValue.trim() : '');

  const handleStart = async () => {
    if (!asn) return;
    setStarting(true);
    setError('');
    try {
      await startImpersonation(String(asn));
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not view as this student');
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>View as student</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            See the app exactly as this student does. Anything you submit is recorded as theirs. Return to
            your account from the banner.
          </Typography>
          <Autocomplete
            freeSolo
            options={options}
            filterOptions={(x) => x}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : `${opt.name} (${opt.asn})`)}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={(_, value) => setSelected(value && typeof value !== 'string' ? value : null)}
            loading={searching}
            renderOption={(props, opt) => (
              <li {...props} key={opt.asn}>
                <Stack>
                  <Typography variant="body2">{opt.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ASN {opt.asn}
                    {opt.school ? ` · ${opt.school}` : ''}
                    {opt.grade ? ` · Gr. ${opt.grade}` : ''}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} autoFocus label="Student" placeholder="Type name or ASN…" />
            )}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" color="warning" disabled={!asn || starting} onClick={handleStart}>
          View as student
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ViewAsStudentDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
